import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  ArrowSquareOut,
  Book,
  Cube,
  GitBranch,
  Info,
  PencilSimple,
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@alfred/convex/_generated/api";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/repos")({
  component: ReposPage,
});

// Mirror of the engine's `isFrontendRepo` (packages/engine/src/ui-verifier.ts)
// so the Repos page can show whether a repo auto-detects as a frontend that
// UI verification will run on. Keep the two in sync.
const FRONTEND_LANGUAGES = new Set([
  "javascript",
  "typescript",
  "vue",
  "svelte",
  "html",
  "css",
  "scss",
  "astro",
]);
const FRONTEND_TOPIC_HINTS = [
  "nuxt",
  "vue",
  "react",
  "next",
  "nextjs",
  "frontend",
  "front-end",
  "ui",
  "widget",
  "svelte",
  "spa",
  "vite",
  "tailwind",
  "storybook",
];
function detectFrontend(r: {
  language?: string;
  topics?: string[];
  name?: string;
}): boolean {
  const lang = (r.language ?? "").toLowerCase();
  if (FRONTEND_LANGUAGES.has(lang)) return true;
  const topics = (r.topics ?? []).map((t) => t.toLowerCase());
  if (topics.some((t) => FRONTEND_TOPIC_HINTS.includes(t))) return true;
  const name = (r.name ?? "").toLowerCase();
  if (/(^|[-_])(nuxt|vue|react|next|widget|ui|web|frontend)([-_]|$)/.test(name)) {
    return true;
  }
  return false;
}

function ReposPage() {
  const repos = useQuery(api.repos.list, {});
  const sync = useMutation(api.repos.requestSync);
  return (
    <div className="mx-auto max-w-6xl px-4 py-6 lg:px-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Repos</h1>
          <p className="text-xs text-muted-foreground">
            GitHub repositories Alfred can clone, branch, and PR against.
            Scoped to whatever the{" "}
            <Link
              to="/brief"
              className="font-medium text-primary hover:underline"
            >
              Tech Brief
            </Link>{" "}
            references — add a <code className="rounded bg-muted px-1 py-0.5">hyrdrocks/&lt;name&gt;</code> mention to include a new repo.
          </p>
        </div>
        <Button variant="outline" onClick={() => sync({})}>
          Sync from GitHub
        </Button>
      </div>
      <Card className="mb-4 border-primary/30 bg-primary/5">
        <CardContent className="flex items-start gap-3 p-3 text-xs">
          <Info
            size={16}
            weight="duotone"
            className="mt-0.5 shrink-0 text-primary"
          />
          <p className="text-muted-foreground">
            Alfred's allowlist is parsed from the Tech Brief on every tick. If
            you remove a repo mention from the brief, it disappears from this
            list on the next sync. To add one, drop a{" "}
            <code className="rounded bg-muted px-1 py-0.5">
              hyrdrocks/&lt;name&gt;
            </code>{" "}
            reference into the brief and press{" "}
            <strong className="text-foreground">Sync from GitHub</strong>.
          </p>
        </CardContent>
      </Card>
      {repos === undefined ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : repos.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            No repos cached yet — click "Sync from GitHub" to fetch the
            organization catalogue.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {repos.map((r) => (
            <RepoCard key={r._id} repo={r} />
          ))}
        </div>
      )}
    </div>
  );
}

interface RepoCardProps {
  repo: {
    _id: string;
    name: string;
    fullName: string;
    url: string;
    description?: string;
    language?: string;
    topics?: string[];
    defaultBranch: string;
    lastSyncedAt: number;
    prRequirements?: string;
    uiVerificationEnabled?: boolean;
    uiVerificationPlaybook?: string;
    prBaseBranch?: string;
    testPlaybook?: string;
  };
}

function RepoCard({ repo: r }: RepoCardProps) {
  const setPrRequirements = useMutation(api.repos.setPrRequirements);
  const setUiVerification = useMutation(api.repos.setUiVerification);
  const setPrBaseBranch = useMutation(api.repos.setPrBaseBranch);
  const setTestPlaybook = useMutation(api.repos.setTestPlaybook);
  const [editingPr, setEditingPr] = React.useState(false);
  const [prDraft, setPrDraft] = React.useState(r.prRequirements ?? "");
  const [editingUi, setEditingUi] = React.useState(false);
  const [uiDraft, setUiDraft] = React.useState(r.uiVerificationPlaybook ?? "");
  const [editingTest, setEditingTest] = React.useState(false);
  const [testDraft, setTestDraft] = React.useState(r.testPlaybook ?? "");
  React.useEffect(() => {
    if (!editingTest) setTestDraft(r.testPlaybook ?? "");
  }, [r.testPlaybook, editingTest]);
  const [baseDraft, setBaseDraft] = React.useState(r.prBaseBranch ?? "");
  React.useEffect(() => {
    setBaseDraft(r.prBaseBranch ?? "");
  }, [r.prBaseBranch]);

  // Keep the draft in sync if Convex pushes a fresh value while we're not
  // editing — otherwise saving a different tab wouldn't reflect.
  React.useEffect(() => {
    if (!editingPr) setPrDraft(r.prRequirements ?? "");
  }, [r.prRequirements, editingPr]);
  React.useEffect(() => {
    if (!editingUi) setUiDraft(r.uiVerificationPlaybook ?? "");
  }, [r.uiVerificationPlaybook, editingUi]);

  async function savePr() {
    try {
      await setPrRequirements({
        fullName: r.fullName,
        value: prDraft.trim() ? prDraft : undefined,
      });
      toast.success(`PR requirements saved for ${r.name}`);
      setEditingPr(false);
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  }

  async function setUiMode(mode: "auto" | "on" | "off") {
    try {
      await setUiVerification({
        fullName: r.fullName,
        enabled: mode === "auto" ? null : mode === "on",
      });
      toast.success(`UI verification set to ${mode} for ${r.name}`);
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  }

  async function saveUiPlaybook() {
    try {
      await setUiVerification({
        fullName: r.fullName,
        playbook: uiDraft.trim() ? uiDraft : "",
      });
      toast.success(`UI playbook saved for ${r.name}`);
      setEditingUi(false);
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  }

  async function saveBaseBranch() {
    const v = baseDraft.trim();
    try {
      await setPrBaseBranch({
        fullName: r.fullName,
        value: v || undefined,
      });
      toast.success(
        v
          ? `PRs for ${r.name} will target \`${v}\``
          : `PR target reset to default (${r.defaultBranch}) for ${r.name}`,
      );
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  }

  async function saveTestPlaybook() {
    try {
      await setTestPlaybook({
        fullName: r.fullName,
        value: testDraft.trim() ? testDraft : undefined,
      });
      toast.success(`Test playbook saved for ${r.name}`);
      setEditingTest(false);
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    }
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-start gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Cube size={18} weight="duotone" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-sm">{r.name}</CardTitle>
            <p className="truncate text-[11px] font-mono text-muted-foreground">
              {r.fullName}
            </p>
          </div>
          <a
            href={r.url}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-primary"
            aria-label="Open in GitHub"
          >
            <ArrowSquareOut size={14} weight="bold" />
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="line-clamp-2 text-xs text-muted-foreground">
          {r.description ?? "(no description)"}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {r.language ? <Badge variant="outline">{r.language}</Badge> : null}
          <Badge variant="muted">
            <GitBranch size={10} weight="bold" /> {r.defaultBranch}
          </Badge>
          {(r.topics ?? []).slice(0, 3).map((t) => (
            <Badge key={t} variant="muted">
              {t}
            </Badge>
          ))}
        </div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Synced {relativeTime(r.lastSyncedAt)}
        </p>

        {/* PR target branch override — where Alfred opens PRs against (and
            bases its work off). Blank = the repo's default branch. */}
        <div className="rounded-lg border border-border/60 bg-muted/30 p-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            PR target branch
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className="flex h-8 flex-1 items-center gap-1.5 rounded-md border border-border bg-background px-2">
              <GitBranch size={11} weight="bold" className="shrink-0 text-muted-foreground" />
              <input
                value={baseDraft}
                onChange={(e) => setBaseDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveBaseBranch();
                }}
                placeholder={`default (${r.defaultBranch})`}
                className="h-full flex-1 bg-transparent font-mono text-xs focus:outline-none"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void saveBaseBranch()}
              disabled={(baseDraft.trim() || undefined) === (r.prBaseBranch ?? undefined)}
            >
              Save
            </Button>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">
            {r.prBaseBranch
              ? `PRs target \`${r.prBaseBranch}\` (override). Clear to use the default.`
              : `Blank → PRs target the default branch (${r.defaultBranch}). Set e.g. \`staging\` to override.`}
          </p>
        </div>

        {/* PR requirements editor — Markdown injected into the planner +
            executor prompts on any run that touches this repo. */}
        <div className="rounded-lg border border-border/60 bg-muted/30 p-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              PR requirements
            </p>
            {editingPr ? (
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={savePr}
                  className="rounded p-1 text-primary hover:bg-primary/10"
                  aria-label="Save"
                >
                  <Check size={12} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPrDraft(r.prRequirements ?? "");
                    setEditingPr(false);
                  }}
                  className="rounded p-1 text-muted-foreground hover:bg-muted"
                  aria-label="Cancel"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingPr(true)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Edit"
              >
                <PencilSimple size={12} weight="bold" />
              </button>
            )}
          </div>
          {editingPr ? (
            <textarea
              value={prDraft}
              onChange={(e) => setPrDraft(e.target.value)}
              rows={6}
              placeholder={`Markdown injected into the agent prompt when this repo is touched.\n\nExample:\n- Flow chart (existing vs new) when flow changes\n- Impacted APIs (URL, payload, params) when API changes\n- Test plan / cases — always`}
              className="mt-1 w-full resize-y rounded-md border border-border bg-background p-2 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-ring/40"
              autoFocus
            />
          ) : r.prRequirements ? (
            <pre className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap text-[10px] font-mono leading-snug text-muted-foreground">
              {r.prRequirements}
            </pre>
          ) : (
            <p className="mt-1 text-[10px] italic text-muted-foreground">
              None. Click the pencil to add repo-specific PR conventions
              (e.g. required sections, checklists).
            </p>
          )}
        </div>

        {/* Test playbook — how Alfred runs this repo's tests locally (esp.
            Docker integration tests). Injected into the executor prompt so
            the agent runs them and makes them pass before finishing. */}
        <div className="rounded-lg border border-border/60 bg-muted/30 p-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Test playbook
            </p>
            {editingTest ? (
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={saveTestPlaybook}
                  className="rounded p-1 text-primary hover:bg-primary/10"
                  aria-label="Save"
                >
                  <Check size={12} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTestDraft(r.testPlaybook ?? "");
                    setEditingTest(false);
                  }}
                  className="rounded p-1 text-muted-foreground hover:bg-muted"
                  aria-label="Cancel"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setEditingTest(true)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Edit"
              >
                <PencilSimple size={12} weight="bold" />
              </button>
            )}
          </div>
          {editingTest ? (
            <textarea
              value={testDraft}
              onChange={(e) => setTestDraft(e.target.value)}
              rows={6}
              placeholder={`How to run this repo's tests locally (Docker available).\n\nExample (paul-api):\nRun the full integration suite:\n  make integration-all\nIf it fails at infra, run the steps individually (integration-infra-reset,\nintegration-migrate, integration-apply, integration-server, integration-test).\nEnsure the suite passes before finishing.`}
              className="mt-1 w-full resize-y rounded-md border border-border bg-background p-2 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-ring/40"
              autoFocus
            />
          ) : r.testPlaybook ? (
            <pre className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap text-[10px] font-mono leading-snug text-muted-foreground">
              {r.testPlaybook}
            </pre>
          ) : (
            <p className="mt-1 text-[10px] italic text-muted-foreground">
              None. Click the pencil to tell Alfred how to run this repo's
              tests locally (esp. Docker integration tests) so it runs them
              before opening a PR instead of waiting on CI.
            </p>
          )}
        </div>

        {/* UI verification — Playwright pass that boots the app, screenshots
            the change, and attaches proof to the PR. Auto-runs on detected
            frontends; Auto/On/Off overrides per repo. */}
        <div className="rounded-lg border border-border/60 bg-muted/30 p-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              UI verification
            </p>
            <div className="flex items-center gap-1">
              {/* Tri-state Auto / On / Off segmented control. */}
              <div className="flex overflow-hidden rounded-md border border-border">
                {(["auto", "on", "off"] as const).map((m) => {
                  const active =
                    r.uiVerificationEnabled === undefined
                      ? m === "auto"
                      : r.uiVerificationEnabled
                        ? m === "on"
                        : m === "off";
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setUiMode(m)}
                      className={
                        "px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors " +
                        (active
                          ? "bg-primary text-primary-foreground"
                          : "bg-transparent text-muted-foreground hover:bg-muted")
                      }
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              {editingUi ? (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={saveUiPlaybook}
                    className="rounded p-1 text-primary hover:bg-primary/10"
                    aria-label="Save playbook"
                  >
                    <Check size={12} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setUiDraft(r.uiVerificationPlaybook ?? "");
                      setEditingUi(false);
                    }}
                    className="rounded p-1 text-muted-foreground hover:bg-muted"
                    aria-label="Cancel"
                  >
                    <X size={12} weight="bold" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingUi(true)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Edit playbook"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
              )}
            </div>
          </div>
          {/* Effective decision line. */}
          <p className="mt-1 text-[10px] text-muted-foreground">
            {(() => {
              const detected = detectFrontend(r);
              if (r.uiVerificationEnabled === true)
                return "✓ Will run (forced on).";
              if (r.uiVerificationEnabled === false)
                return "✕ Won't run (forced off).";
              return detected
                ? "✓ Will run — auto-detected as a frontend."
                : "— Auto: not detected as a frontend, so it won't run. Set On to force.";
            })()}
          </p>
          {editingUi ? (
            <textarea
              value={uiDraft}
              onChange={(e) => setUiDraft(e.target.value)}
              rows={6}
              placeholder={`How should Alfred boot THIS app to verify a change?\n\nExample:\n- Install: npm ci\n- Start: cp .env.stub .env && npm run dev (serves http://localhost:3000)\n- Wait for the homepage to render, then exercise the changed screen\n- Stub backend: VITE_API_BASE=http://localhost:4010 (prism mock)`}
              className="mt-1 w-full resize-y rounded-md border border-border bg-background p-2 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-ring/40"
              autoFocus
            />
          ) : r.uiVerificationPlaybook ? (
            <pre className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap text-[10px] font-mono leading-snug text-muted-foreground">
              {r.uiVerificationPlaybook}
            </pre>
          ) : (
            <p className="mt-1 text-[10px] italic text-muted-foreground">
              No boot playbook — Alfred best-effort auto-detects how to run
              it. Click the pencil to document install/start/URL for reliable
              proof.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
